import { gql, useQuery } from '@apollo/client';
import ProductionByMonthStackedChartThumbnail from '@assets/images/report-components/production-by-month-stacked-chart-thumbnail.png';
import { useMemo, useState } from 'react';
import { getLayout } from '@/admins/SmartReports/components/Components/Charts/helpers';
import { ComponentBox } from '@/admins/SmartReports/components/Components/Common/ComponentBox';
import { ComponentTitle } from '@/admins/SmartReports/components/Components/Common/ComponentTitle';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import Graph from '@/components/Graphs/graph';
import { useChartColors } from '@/hooks/useChartColors';
import type { AssetAggregatesStacked } from '@/types/GraphqlSchema';
import { LoadingErrorWrapper } from '../Common/LoadingErrorWrapper';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '../types';

const QUERY = gql`
  query assetAggregatesStacked($input: AssetAggregatesInput!) {
    assetAggregatesStacked(input: $input) {
      data {
        type
        group
        values
      }
      stacks
    }
  }
`;

export const COMPONENT_ID = 'production_by_month_stacked_chart';

const useData = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: UseDataType<AssetAggregatesStacked>) => {
  const [chartDataFetched, setChartDataFetched] = useState(persistedData);

  const { loading, error } = useQuery<{ assetAggregatesStacked: AssetAggregatesStacked }>(QUERY, {
    variables: {
      input: {
        by: 'productionMonth',
        fields: ['totalProductionsSum'],
        stackBy: 'country',
        portfolio,
      },
    },
    skip: !!persistedData,
    onCompleted: response => {
      if (response?.assetAggregatesStacked) {
        const chartData = response.assetAggregatesStacked;
        setChartDataFetched(chartData);
        if (onComponentLoad) {
          onComponentLoad({
            componentId: COMPONENT_ID,
            data: chartData,
          });
        }
      }
    },
  });

  return { chartData: chartDataFetched, error, loading };
};

const ComponentView = ({ data }: ComponentViewProps<AssetAggregatesStacked>) => {
  const chartColors = useChartColors();
  const formattedData = useMemo(() => {
    try {
      const { data: chartData, stacks } = data;

      const formattedChartData = stacks.map((stackName, idx) => {
        const x = chartData.map(item => item.group); // Time periods (e.g., "Jan 2024")
        const y = chartData.map(item => {
          const stackValues = item.values[stackName];
          if (stackValues) {
            return stackValues.totalProductionsSum || 0;
          }
          return 0;
        });

        return {
          x,
          y,
          type: 'scatter',
          name: stackName,
          stackgroup: 'stack',
          line: { color: chartColors[idx % chartColors.length] },
          fillcolor: chartColors[idx % chartColors.length],
        };
      });

      return formattedChartData;
    } catch {
      return null;
    }
  }, [data, chartColors]);

  if (!formattedData) return null;

  return (
    <ComponentBox>
      <ComponentTitle>Production by Month (Stacked by Country)</ComponentTitle>
      <Graph
        data={formattedData}
        layout={getLayout({ yaxis: { title: { text: 'boe/d' } }, legend: { y: -0.2 } })}
        title={undefined}
        config={undefined}
        xlabel={undefined}
        ylabel={undefined}
        loading={undefined}
        onUpdate={undefined}
        onClick={undefined}
        onRelayout={undefined}
        onInitialized={undefined}
        onAfterPlot={undefined}
        maxY={undefined}
        darkTheme={undefined}
        context={undefined}
      />
    </ComponentBox>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<AssetAggregatesStacked>) => {
  const { chartData, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={chartData}>
      {chartData && <ComponentView data={chartData} />}
    </LoadingErrorWrapper>
  );
};

const ComponentThumbnail = () => (
  <ComponentBox>
    <ComponentTitle sx={{ fontSize: '0.25rem', marginBottom: 0 }}>
      Production By Month Chart
    </ComponentTitle>
    <img
      src={ProductionByMonthStackedChartThumbnail}
      alt='Production By Month Stacked Chart Thumbnail'
      style={{ width: '100%', height: 'auto' }}
    />
  </ComponentBox>
);

export const ProductionByMonthStackedChartComponent: ComponentDefinition<AssetAggregatesStacked> = {
  id: COMPONENT_ID,
  name: 'Production by Month',
  type: SmartReportComponentTypesEnum.Chart,
  reportTypes: [SmartReportTypeEnum.Corporate],
  Thumbnail: ComponentThumbnail,
  Component: ComponentEdit,
  ComponentView,
};
